import styled from "styled-components";
import { extractFloatString } from "utils/general";
import { getLocaleFloatString, toFixed } from "@shared/utils/numbers";
import Input from "common/Inputs/Input";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import AutoSizeInput from "common/Inputs/AutoSizeInput";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback";
import { hexToRGB } from "@shared/utils/misc";

const CustomInputNumber = React.forwardRef(
  (
    {
      bordered = true,
      defaultValue,
      onChange,
      precision = 0,
      value,
      width,
      minWidth,
      stringMode,
      onFocus,
      onBlur,
      placeholder,
      focusOnMount,
      underline,
      centerText,
      useDebounce,
      autoSize,
      innerRef,
      hideArrows,
      color,
      bgColor,
      suffix,
      focusDelay = 0,
      ...props
    }: any,
    ref
  ) => {
    const [localValue, setLocalValue] = useState(value);
    const valueToUse = getLocaleFloatString(useDebounce ? localValue : value);
    const localInputRef = useRef<HTMLInputElement>(undefined);
    const inputRef = ref ?? (innerRef || localInputRef);

    useEffect(() => {
      if (
        useDebounce &&
        (value === undefined ||
          value === null ||
          isNaN(value) ||
          parseFloat(value) !== parseFloat(localValue?.toString()))
      ) {
        setLocalValue(value);
      }
    }, [value]);

    useEffect(() => {
      if (focusOnMount) {
        setTimeout(() => {
          inputRef?.current?.focus();
        }, focusDelay);
      }
    }, [inputRef]);

    const _onFocus = () => {
      onFocus?.();
    };

    const inputProps: any = {};
    if (!valueToUse && placeholder) {
      inputProps.value = "";
      inputProps.placeholder = placeholder;
    } else {
      inputProps.value = valueToUse ?? "";
    }

    const style: CSSProperties = {};
    if (!autoSize) {
      style.width = width || "100%";
      style.minWidth = minWidth;
    } else {
      style.paddingLeft = 0;
      style.paddingRight = 0;
    }

    const debouncedOnChange = useDebouncedCallback({ callback: onChange });
    const _onChange = (inputValue: string) => {
      let parsedValue: any;
      if (inputValue !== "") {
        parsedValue = extractFloatString(inputValue, precision || 0);
        if (!stringMode) {
          parsedValue = toFixed(parseFloat(parsedValue), precision || 0);
        }
      }
      setLocalValue(parsedValue);
      useDebounce ? debouncedOnChange(parsedValue) : onChange(parsedValue);
    };

    const _onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      debouncedOnChange.flush();
      onBlur?.(event);
    };

    const inputMode = precision ? "decimal" : "numeric";
    const inputComponent = bordered ? (
      <StyledAntInput
        ref={inputRef}
        $hideArrows={hideArrows}
        onChange={(e) => _onChange(e.target.value)}
        onFocus={_onFocus}
        onBlur={_onBlur}
        style={style}
        $color={color}
        $bgColor={bgColor}
        inputMode={inputMode}
        suffix={suffix}
        {...props}
        {...inputProps}
      />
    ) : (
      <StyledInput
        ref={inputRef}
        type={stringMode ? "text" : "number"}
        $bordered={bordered}
        $underline={underline}
        $centerText={centerText}
        $color={color}
        $bgColor={bgColor}
        onChange={(e) => _onChange(e.target.value)}
        onFocus={_onFocus}
        onBlur={_onBlur}
        style={style}
        inputMode={inputMode}
        {...props}
        {...inputProps}
      />
    );
    return autoSize ? (
      <AutoSizeInput
        value={inputProps.value || placeholder}
        minWidth={minWidth}
        input={inputComponent}
      />
    ) : (
      inputComponent
    );
  }
);

export default CustomInputNumber;

const StyledAntInput = styled(Input)<{ $hideArrows: boolean }>`
  ${(p) =>
    p.$hideArrows &&
    `
  .ant-input-number-handler-wrap {
    display: none;
  }
`}
`;

const StyledInput = styled.input<{
  $bordered: boolean;
  $underline: boolean;
  $centerText: boolean;
  $bgColor: string;
  $color: string;
}>`
  width: ${(p) => (p.width ? `${p.width}px` : "100%")};
  font-weight: inherit;
  font-size: inherit;
  font-family: inherit;
  ${(p) =>
    p.$bordered
      ? ``
      : `
    border: none;
    &:focus {
      outline: none;
    }
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      margin: 0;
    }
  `}
  ${(p) =>
    p.$underline &&
    `
    border-bottom: 1px solid ${hexToRGB(p.theme.colors.secondaryText, 0.5)};
  `}
  ${(p) =>
    p.$centerText &&
    `
    text-align: center;
  `};
  background: ${(p) => p.$bgColor || p.theme.colors.cardBgColor}!important;
  color: ${(p) => p.$color || p.theme.colors.textColor};
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px ${(p) => p.theme.colors.cardBgColor} inset !important;
    -webkit-text-fill-color: ${(p) =>
      p.$color || p.theme.colors.textColor} !important;
  }
  &::placeholder {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }
`;
